import { useMemo } from 'react'
import Modal from '@mui/material/Modal'

interface IRunScenarioConfirmationModal {
  isModalOpen: boolean
  title: string
  description: string[]
  confirmLabel?: string
  onConfirm?: () => void
  onCancel?: () => void
  cancelLabel?: string
  isLoading?: boolean
}

const RunScenarioConfirmationModal = ({
  isModalOpen,
  title,
  description,
  confirmLabel,
  onConfirm,
  onCancel,
  cancelLabel = 'CANCEL',
  isLoading = false
}: IRunScenarioConfirmationModal) => {
  // build the key

  const keyedDescription = useMemo(() => {
    const counts = new Map<string, number>()

    return description.map((line) => {
      const occurrence = counts.get(line) ?? 0
      counts.set(line, occurrence + 1)

      return { key: `${line}-${occurrence}`, line }
    })
  }, [description])

  return (
    <Modal
      open={isModalOpen}
      className='flex items-center justify-center'
      onClose={(_event: React.SyntheticEvent, reason: string) => {
        if (isLoading) {
          return
        }

        if (reason !== 'backdropClick' && onCancel != null) {
          onCancel()
        }
      }}
    >
      <div className='max-w-lg rounded bg-white p-8 text-center'>
        <div className='pb-4 text-xl font-bold'>{title}</div>

        {isLoading && (
          <div className='mb-3 flex justify-center'>
            <div className='h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-lighthouseBlue' />
          </div>
        )}

        <div className='space-y-4 pb-6 text-gray-700'>
          {keyedDescription.map(({ key, line }) => (
            <p key={key}>{line}</p>
          ))}
        </div>

        <div className='flex justify-center gap-4'>
          {onCancel != null && (
            <button
              className='w-28 rounded border border-gray-300 px-4 py-2 font-semibold uppercase text-gray-700 disabled:opacity-60'
              onClick={onCancel}
              disabled={isLoading}
            >
              {cancelLabel}
            </button>
          )}
          <button
            className='w-28 rounded bg-lighthouseBlue px-4 py-2 font-semibold uppercase text-white disabled:opacity-60'
            onClick={onConfirm}
            disabled={isLoading}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </Modal>
  )
}

export default RunScenarioConfirmationModal
